import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type FontOptions,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { ConnectionSelectorComponent } from './connection-selector.component';

export type ConnectionSelectorDef = WithProps<ConnectionSelectorPropsModel> & WithIoConfig;
export type ConnectionSelectorElement = CanvasElementFrom<'connectionSelector'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    connectionSelector: ConnectionSelectorDef;
  }
}

const DEFAULT_WIDTH = 360;
const MIN_SIZE = 58;

registerElementDefinition({
  type: 'connectionSelector',
  name: 'Connection Selector',
  componentType: ConnectionSelectorComponent,
  defaults: getDefaults(),
  ioConfig: getIoConfig(),
  isListed: true,
  propsConfig: getPropsConfig(),
  tags: ['connector', 'api'],
  toolbar: ['triggerClick'],
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: false },
  groups: ['advanced'],
  version: 1,
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'click', schema: null }],
  };
}

type PropsModel = CanvasElementPropertiesWithData & VisibilityElementProps;

export type ConnectionSelectorPropsModel = PropsModel & {
  content: {
    connectionSelector: {
      connectorLabel: string;
      hideIcon: boolean;
      selectorId: string;
    };
  };
  style: {
    container: {
      color: PropertiesConfigInputColorModel;
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      width: ValueWithUnitOfMeasure;
    };
    button: {
      buttonType: string;
      primary: {
        color: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        radius: BorderRadiusValue;
        width: ValueWithUnitOfMeasure;
      };
      secondary: {
        color: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        radius: BorderRadiusValue;
        width: ValueWithUnitOfMeasure;
      };
    };
    font: FontOptions;
  };
};

function getDefaults(): PropsOf<'connectionSelector'> {
  return {
    content: {
      connectionSelector: {
        connectorLabel: '',
        hideIcon: false,
        selectorId: '',
      },
    },
    style: {
      button: {
        buttonType: 'primarybutton',
        primary: {
          color: { color1: '$t.fill', selectorType: 'solid' },
          fill: { color1: '$t.accent', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'none' },
          radius: '$t.radius',
          width: '1px',
        },
        secondary: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fill: { color1: '$t.complementary', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'solid' },
          radius: '$t.radius',
          width: '1px',
        },
      },
      container: {
        color: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
        width: '1px',
      },

      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        lineHeight: '$t.lineHeight',
        fontSize: '$t.fontSize',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('connectionSelector', [
    // Content
    epc.section('content', 'Content', [
      epc.subsections.subsection('content.connectionSelector', 'Connect a Service', [
        epc.fields.selectFieldFromApi(
          'content.connectionSelector.selectorId',
          'Service',
          'LIST_CONNECTION_SELECTOR_CONNECTORS',
          {
            showSelection: true,
            allowNone: false,
            hideLabel: true,
            placeholder: 'Select a Service',
          },
        ),
      ]),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.textInput('content.connectionSelector.connectorLabel', 'Connector Label', {
          hideLabel: true,
          placeholder: 'Connect to [service name]',
        }),
        epc.fields.toggleField('content.connectionSelector.hideIcon', 'Hide connector icon'),
      ]),
      epc.subsections.visibilitySubsection(),
    ]),

    // Style
    epc.section(
      'style',
      'Style',
      [
        // Container
        epc.subsections.subsection('style.container', 'Container', [
          epc.fields.colorField('style.container.color', 'Label color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.utils.separatedField(
            epc.fields.colorField('style.container.fill', 'Fill', { allowGradient: false }),
            { fullWidthSeparator: true },
          ),
          epc.fields.colorField('style.container.outline', 'Outline', { allowGradient: false }),
          epc.fields.sliderField('style.container.width', 'Width', [
            { min: 0, max: 25, unit: 'px' },
          ]),
          epc.fields.radiusField('style.container.radius'),
        ]),

        // Buttons
        epc.subsections.subsection(
          'style.button',
          '',
          [
            epc.fields.radiosField(
              'style.button.buttonType',
              'Button Type',
              [
                { value: 'primary', label: 'Primary Button' },
                { value: 'secondary', label: 'Secondary Button' },
              ],
              { hideLabel: true },
            ),

            // Primary button
            ...epc.utils.conditionalFields(
              [
                epc.fields.colorField('style.button.primary.color', 'Label color', {
                  allowGradient: false,
                  allowNone: false,
                }),
                epc.utils.separatedField(
                  epc.fields.colorField('style.button.primary.fill', 'Fill', {
                    allowGradient: false,
                  }),
                  { fullWidthSeparator: true, hideSeparatorWhenHidden: true },
                ),
                epc.fields.colorField('style.button.primary.outline', 'Outline', {
                  allowGradient: false,
                }),
                epc.fields.sliderField('style.button.primary.width', 'Width', [
                  { min: 0, max: 25, unit: 'px' },
                ]),
                epc.fields.radiusField('style.button.primary.radius'),
              ],
              'style.button.buttonType',
              (type: 'primary' | 'secondary') => type === 'secondary',
            ),

            // Secondary button
            ...epc.utils.conditionalFields(
              [
                epc.fields.colorField('style.button.secondary.color', 'Label color', {
                  allowGradient: false,
                  allowNone: false,
                }),
                epc.utils.separatedField(
                  epc.fields.colorField('style.button.secondary.fill', 'Fill', {
                    allowGradient: false,
                  }),
                  { fullWidthSeparator: true, hideSeparatorWhenHidden: true },
                ),
                epc.fields.colorField('style.button.secondary.outline', 'Outline', {
                  allowGradient: false,
                }),
                epc.fields.sliderField('style.button.secondary.width', 'Width', [
                  { min: 0, max: 25, unit: 'px' },
                ]),
                epc.fields.radiusField('style.button.secondary.radius'),
              ],
              'style.button.buttonType',
              (type: 'primary' | 'secondary') => type === 'primary',
            ),
          ],
          { hideLabel: true },
        ),
      ],
      true,
    ),
  ]);
}
